import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Check, Gift as GiftIcon, Send, Heart } from "lucide-react";
import { Chapter, MaskLines, Reveal } from "./Reveal";
import { HeartIcon } from "./Deco";
import { WEDDING } from "../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatIban = (raw) => (raw || "").replace(/(.{4})/g, "$1 ").trim();

const Field = ({ label, children }) => (
  <div>
    <label className="font-sans text-[11px] tracking-[0.2em] uppercase text-[var(--gold)] block mb-2">{label}</label>
    {children}
  </div>
);

export const Gift = ({ t }) => {
  const [copied, setCopied] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const copy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      toast.success(t.copied);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t.required);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/gift-note`, {
        name: name.trim(),
        message: message.trim(),
      });
      setDone(true);
      toast.success(t.success);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.error);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setName("");
    setMessage("");
    setDone(false);
  };

  return (
    <section id="gift" className="relative paper py-24 sm:py-32 px-5">
      <div className="max-w-xl mx-auto text-center">
        <Chapter>{t.chapter}</Chapter>
        <div className="flex items-center justify-center gap-3">
          <HeartIcon size={16} className="text-[var(--gold-dark)]" />
          <MaskLines lines={[t.title]} className="font-serif text-4xl sm:text-6xl text-[var(--forest)]" />
          <HeartIcon size={16} className="text-[var(--gold-dark)]" />
        </div>
        <Reveal delay={0.1}>
          <p className="font-sans text-sm text-[var(--olive)]/70 mt-4 max-w-md mx-auto">{t.note}</p>
        </Reveal>

        {/* IBAN card */}
        <Reveal delay={0.15}>
          <div className="mt-10 rounded-2xl leather p-6 sm:p-8 text-left shadow-xl" data-testid="iban-card">
            <div className="flex items-center gap-2 mb-5 text-[var(--gold)]">
              <GiftIcon size={18} />
              <span className="font-sans text-[11px] tracking-[0.25em] uppercase">Banka Havalesi / Bank Transfer</span>
            </div>

            <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[var(--gold-light)]/60">{t.nameLabel}</p>
            <div className="flex items-center justify-between gap-3 mt-1 pb-4 border-b border-[var(--gold)]/20">
              <span className="font-serif text-xl text-[var(--cream)]" data-testid="iban-name">{WEDDING.ibanName}</span>
              <CopyBtn onClick={() => copy(WEDDING.ibanName, "name")} active={copied === "name"} label={t.copyName} testid="copy-name-btn" />
            </div>

            <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[var(--gold-light)]/60 mt-4">{t.ibanLabel}</p>
            <div className="flex items-center justify-between gap-3 mt-1">
              <span className="font-mono text-sm sm:text-base text-[var(--cream)] tracking-wide break-all" data-testid="iban-value">
                {formatIban(WEDDING.iban)}
              </span>
            </div>
            <button
              data-testid="copy-iban-btn"
              onClick={() => copy(WEDDING.iban, "iban")}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-full font-sans tracking-[0.18em] text-xs uppercase text-[var(--olive)] bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#B38728] transition-transform duration-300 hover:scale-[1.02] active:scale-95"
            >
              {copied === "iban" ? <Check size={15} /> : <Copy size={15} />}
              {copied === "iban" ? t.copied : t.copyIban}
            </button>
          </div>
        </Reveal>

        {/* Blessing note */}
        <Reveal delay={0.2}>
          {done ? (
            <div className="mt-8 rounded-2xl leather p-10 flex flex-col items-center" data-testid="gift-success">
              <motion.div
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 220, damping: 12 }}
                className="w-16 h-16 rounded-full bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#B38728] flex items-center justify-center mb-5"
              >
                <Heart size={26} className="text-[var(--forest)]" fill="currentColor" />
              </motion.div>
              <p className="gold-text font-serif text-2xl">{t.success}</p>
              <button
                data-testid="gift-again-btn"
                onClick={reset}
                className="mt-7 inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-[var(--gold)]/50 text-[var(--gold)] font-sans tracking-[0.18em] text-[11px] uppercase hover:bg-[var(--olive)]/40 transition-colors"
              >
                {t.again}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 rounded-2xl leather p-6 sm:p-9 text-left space-y-6 shadow-xl" data-testid="gift-form">
              <div className="flex items-center gap-2 text-[var(--gold)]">
                <HeartIcon size={14} className="text-[var(--gold)]" />
                <span className="font-sans text-[11px] tracking-[0.25em] uppercase">{t.noteTitle}</span>
              </div>
              <Field label={t.name}>
                <input
                  data-testid="gift-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.namePh}
                  className="w-full bg-transparent border-b border-[var(--gold)]/40 py-2 font-serif text-lg text-[var(--cream)] outline-none focus:border-[var(--gold)] transition-colors placeholder:text-[var(--gold-light)]/50"
                />
              </Field>
              <Field label={t.message}>
                <textarea
                  data-testid="gift-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t.messagePh}
                  rows={3}
                  className="w-full bg-transparent border-b border-[var(--gold)]/40 py-2 font-serif text-lg text-[var(--cream)] outline-none focus:border-[var(--gold)] transition-colors resize-none placeholder:text-[var(--gold-light)]/50"
                />
              </Field>
              <button
                data-testid="gift-submit"
                type="submit"
                disabled={sending}
                className="group w-full flex items-center justify-center gap-3 py-3.5 rounded-full font-sans tracking-[0.18em] text-xs uppercase text-[var(--olive)] bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#B38728] transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                <Send size={15} /> {sending ? t.sending : t.submit}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

const CopyBtn = ({ onClick, active, label, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--gold)]/50 text-[var(--gold)] text-[10px] tracking-[0.15em] uppercase hover:bg-[var(--olive)]/50 transition-colors"
    title={label}
  >
    {active ? <Check size={12} /> : <Copy size={12} />}
    {active ? "OK" : "Kopyala"}
  </button>
);
